import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { DEFAULT_ACCOUNT_TRIP_FILTERS, type AccountTrip, type AccountTripFilters, type AuthSession, type LoginCredentials, type RegistrationDetails } from '../../api'
import { formatElapsed } from '../../app/tripTimeline'
import appStoreBadge from '../../assets/store/app-store-badge.png'
import googlePlayBadge from '../../assets/store/google-play-badge.png'
import { BrandMark, DesktopHeaderBrand, HeaderActionsMenu, LoadingBrandPulse, SettingsGearIcon, TripActionsMenu, UserAvatar } from '../../components'
import { formatDistance, formatSpeed, type UnitSystem } from '../../preferences'
import './account.css'
import { formatTripStarted } from './tripStarted'
import { homeHref, publicInformationMenuLinks } from '../../app/publicRoutes'
import { shareUrlForTrip } from './TripDialogs'

function ProductBenefits() {
  return (
    <ul className="account-benefits" aria-label="Mapapal benefits">
      <li><span aria-hidden="true">●</span><strong>Live location</strong><small>See progress as it happens</small></li>
      <li><span aria-hidden="true">◇</span><strong>Private by choice</strong><small>You control who can follow</small></li>
      <li><span aria-hidden="true">↗</span><strong>One simple link</strong><small>No app needed to follow</small></li>
    </ul>
  )
}

function StoreLinks() {
  return (
    <div className="account-store-links" aria-label="Download Mapapal">
      <a href="https://apps.apple.com/app/mapapal-real-time-gps-tracker/id590277521" target="_blank" rel="noreferrer" aria-label="Download Mapapal on the App Store">
        <img src={appStoreBadge} alt="Download on the App Store" />
      </a>
      <a href="https://play.google.com/store/apps/details?id=com.mapapal.mapapal_free" target="_blank" rel="noreferrer" aria-label="Get Mapapal on Google Play">
        <img src={googlePlayBadge} alt="Get it on Google Play" />
      </a>
    </div>
  )
}

type TripListLayout = 'list' | 'grid'
const TRIP_LIST_LAYOUT_KEY = 'mapapal.trip-list-layout'
const DESKTOP_TRIP_LIST_WIDTH_KEY = 'mapapal.desktop-trip-list-width'

function desktopTripListWidthBounds(viewportWidth: number): { minimum: number; maximum: number } {
  const defaultWidth = Math.min(544, Math.max(336, viewportWidth * .36))
  const minimum = Math.max(336, defaultWidth - 50)
  return { minimum, maximum: Math.max(minimum, Math.min(defaultWidth + 50, viewportWidth * .44)) }
}

function defaultDesktopTripListWidth(viewportWidth: number): number {
  const { minimum, maximum } = desktopTripListWidthBounds(viewportWidth)
  return Math.min(maximum, Math.max(minimum, Math.min(544, Math.max(336, viewportWidth * .36))))
}

function readDesktopTripListWidth(): number {
  const fallback = defaultDesktopTripListWidth(window.innerWidth)
  try {
    const stored = Number(window.localStorage.getItem(DESKTOP_TRIP_LIST_WIDTH_KEY))
    if (!Number.isFinite(stored) || stored <= 0) return fallback
    const { minimum, maximum } = desktopTripListWidthBounds(window.innerWidth)
    return Math.min(maximum, Math.max(minimum, stored))
  } catch {
    return fallback
  }
}

function storeDesktopTripListWidth(width: number): void {
  try {
    window.localStorage.setItem(DESKTOP_TRIP_LIST_WIDTH_KEY, String(Math.round(width)))
  } catch {
    // The resized width remains active for this page if storage is unavailable.
  }
}

function readTripListLayout(): TripListLayout {
  try {
    return window.localStorage.getItem(TRIP_LIST_LAYOUT_KEY) === 'grid' ? 'grid' : 'list'
  } catch {
    return 'list'
  }
}

function storeTripListLayout(layout: TripListLayout): void {
  try {
    window.localStorage.setItem(TRIP_LIST_LAYOUT_KEY, layout)
  } catch {
    // The choice remains active for this page if storage is unavailable.
  }
}

function TripTitle({ title, untitled }: { title: string; untitled: boolean }) {
  return (
    <div className="account-trip__title-row">
      <span className={`account-trip__title${untitled ? ' account-trip__title--untitled' : ''}`}>
        {title}
      </span>
    </div>
  )
}

function filtersAreActive(filters: AccountTripFilters): boolean {
  return Boolean(
    filters.freeText.trim()
    || filters.fromDate
    || filters.toDate
    || !filters.includeOwn
    || !filters.includePals
    || !filters.includePublic,
  )
}

function TripListFilters({
  filters,
  loading,
  tripCount,
  onApply,
}: {
  filters: AccountTripFilters
  loading: boolean
  tripCount?: number
  onApply: (filters: AccountTripFilters) => void
}) {
  const [draft, setDraft] = useState<AccountTripFilters>(() => ({ ...filters }))
  const sourcesSelected = draft.includeOwn || draft.includePals || draft.includePublic

  useEffect(() => {
    setDraft({ ...filters })
  }, [filters.freeText, filters.includeOwn, filters.includePals, filters.includePublic])

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!sourcesSelected) return
    onApply({ ...draft, freeText: draft.freeText.trim(), fromDate: '', toDate: '' })
  }

  return (
    <form className="account-trip-filters" role="search" aria-label="Search and filter trips" onSubmit={submit}>
      <div className="account-trip-filters__search">
        <input
          type="search"
          value={draft.freeText}
          aria-label="Search trips"
          placeholder="Search trips or people"
          onChange={(event) => setDraft((current) => ({ ...current, freeText: event.target.value }))}
        />
        <fieldset aria-label="Trip sources">
          <label><input type="checkbox" checked={draft.includeOwn} onChange={(event) => setDraft((current) => ({ ...current, includeOwn: event.target.checked }))} /><span>Mine</span></label>
          <label><input type="checkbox" checked={draft.includePals} onChange={(event) => setDraft((current) => ({ ...current, includePals: event.target.checked }))} /><span>Pals</span></label>
          <label><input type="checkbox" checked={draft.includePublic} onChange={(event) => setDraft((current) => ({ ...current, includePublic: event.target.checked }))} /><span>Public</span></label>
        </fieldset>
        <button className="account-trip-filters__submit" type="submit" aria-label="Run trip search" title="Search" disabled={loading || !sourcesSelected}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="5.5" /><path d="m15 15 4.5 4.5" /></svg>
        </button>
      </div>
      {tripCount !== undefined ? <output className="account-trip-filters__count" aria-label="Trip count">{tripCount} {tripCount === 1 ? 'trip' : 'trips'}</output> : null}
    </form>
  )
}

export function AccountLoadingView() {
  return (
    <main className="account-shell account-trips account-shell--centered" aria-busy="true" aria-label="Checking your session">
      <div className="account-trip-loading__brand" aria-hidden="true">
        <LoadingBrandPulse className="account-trip-loading__brand-icon" />
        <span>Loading..</span>
      </div>
    </main>
  )
}

export function LoginView({
  pending,
  error,
  notice,
  showDemoCredentials,
  onSubmit,
  onRegister,
  onForgotPassword,
}: {
  pending: boolean
  error?: string
  notice?: string
  showDemoCredentials?: boolean
  onSubmit: (credentials: LoginCredentials) => void
  onRegister: () => void
  onForgotPassword: () => void
}) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!username.trim() || !password) return
    onSubmit({ username: username.trim(), password })
  }

  return (
    <main className="account-shell account-login">
      <div className="account-login__glow" aria-hidden="true" />
      <div className="account-login__layout">
        <section className="account-login__card" aria-labelledby="login-title">
          <BrandMark href={import.meta.env.BASE_URL} />
          <p className="account-eyebrow">Your journeys, in one place</p>
          <h2 id="login-title">Sign in to Mapapal</h2>
          <p className="account-login__intro">View the trips you record and share with the Mapapal apps.</p>

          <form className="account-form" onSubmit={submit}>
            <label>
              <span>Username</span>
              <input type="text" value={username} onChange={(event) => setUsername(event.currentTarget.value)} autoComplete="username" autoCapitalize="none" spellCheck="false" required />
            </label>
            <label>
              <span>Password</span>
              <input type="password" value={password} onChange={(event) => setPassword(event.currentTarget.value)} autoComplete="current-password" required />
            </label>
            {error ? <div className="account-form__error" role="alert">{error}</div> : null}
            {notice ? <div className="account-form__notice" role="status">{notice}</div> : null}
            <button className="account-primary-button" type="submit" disabled={pending || !username.trim() || !password}>{pending ? 'Signing in…' : 'Sign in'}</button>
          </form>

          <div className="account-access-links">
            <button type="button" onClick={onForgotPassword}>Forgot username or password?</button>
            <button type="button" onClick={onRegister}>Create an account</button>
          </div>

          {showDemoCredentials ? <p className="account-demo-hint">Demo login: <strong>demo</strong> / <strong>mapapal</strong></p> : null}
        </section>
        <section className="account-login__story" aria-labelledby="mapapal-story-title">
          <div className="account-login__story-content">
            <span className="account-story__badge"><i aria-hidden="true" /> Live trip sharing</span>
            <p className="account-eyebrow">Stay connected on every journey</p>
            <h1 id="mapapal-story-title">Share the journey, not just the destination.</h1>
            <p>Start a GPS trip with Mapapal for iPhone, iPad or Android. Share one link and let friends or family follow your progress live from any browser.</p>
            <ProductBenefits />
            <StoreLinks />
          </div>
        </section>
      </div>
    </main>
  )
}

export function RegisterView({
  pending,
  error,
  onSubmit,
  onBack,
}: {
  pending: boolean
  error?: string
  onSubmit: (details: RegistrationDetails) => void
  onBack: () => void
}) {
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!username.trim() || !email.trim() || !password) return
    onSubmit({ username: username.trim(), email: email.trim(), password })
  }

  return (
    <main className="account-shell account-login">
      <div className="account-login__glow" aria-hidden="true" />
      <section className="account-login__card" aria-labelledby="register-title">
        <BrandMark />
        <p className="account-eyebrow">Join Mapapal</p>
        <h1 id="register-title">Create account</h1>
        <p className="account-login__intro">Create an account for the Mapapal apps and web.</p>
        <form className="account-form" onSubmit={submit}>
          <label><span>Username</span><input value={username} onChange={(event) => setUsername(event.currentTarget.value)} autoComplete="username" autoCapitalize="none" spellCheck="false" required /></label>
          <label><span>Email</span><input type="email" value={email} onChange={(event) => setEmail(event.currentTarget.value)} autoComplete="email" required /></label>
          <label><span>Password</span><input type="password" value={password} onChange={(event) => setPassword(event.currentTarget.value)} autoComplete="new-password" required /></label>
          {error ? <div className="account-form__error" role="alert">{error}</div> : null}
          <button className="account-primary-button" type="submit" disabled={pending || !username.trim() || !email.trim() || !password}>{pending ? 'Creating account…' : 'Create account'}</button>
        </form>
        <button className="account-back-link" type="button" onClick={onBack}>← Back to sign in</button>
      </section>
    </main>
  )
}

export function ForgotPasswordView({
  pending,
  error,
  success,
  onSubmit,
  onBack,
}: {
  pending: boolean
  error?: string
  success?: string
  onSubmit: (usernameOrEmail: string) => void
  onBack: () => void
}) {
  const [usernameOrEmail, setUsernameOrEmail] = useState('')

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!usernameOrEmail.trim()) return
    onSubmit(usernameOrEmail.trim())
  }

  return (
    <main className="account-shell account-login">
      <div className="account-login__glow" aria-hidden="true" />
      <section className="account-login__card" aria-labelledby="forgot-title">
        <BrandMark />
        <p className="account-eyebrow">Account recovery</p>
        <h1 id="forgot-title">Forgot your login?</h1>
        <p className="account-login__intro">Enter your username or email and Mapapal will send recovery instructions to the registered email address.</p>
        <form className="account-form" onSubmit={submit}>
          <label><span>Username or email</span><input value={usernameOrEmail} onChange={(event) => setUsernameOrEmail(event.currentTarget.value)} autoComplete="username" autoCapitalize="none" required /></label>
          {error ? <div className="account-form__error" role="alert">{error}</div> : null}
          {success ? <div className="account-form__notice" role="status">{success}</div> : null}
          <button className="account-primary-button" type="submit" disabled={pending || !usernameOrEmail.trim()}>{pending ? 'Sending request…' : 'Send recovery instructions'}</button>
        </form>
        <button className="account-back-link" type="button" onClick={onBack}>← Back to sign in</button>
      </section>
    </main>
  )
}

function TripStatus({ status }: { status: AccountTrip['status'] }) {
  const label = status === 'running' ? 'In progress' : status === 'paused' ? 'Paused' : 'Completed'
  return <span className={`account-trip__status account-trip__status--${status}`}>{label}</span>
}

function visibilityLabel(visibility: AccountTrip['visibility']): string {
  return {
    private: 'Private',
    pals: 'Shared with pals',
    public: 'Public',
    unknown: 'Sharing unknown',
  }[visibility]
}

export function TripListView({
  session,
  trips,
  loading,
  loadingEarlier,
  hasEarlier,
  error,
  earlierError,
  onRetry,
  onLoadEarlier,
  onOpenProfile,
  onOpenTrip,
  onShareTrip,
  onEditTrip,
  filters,
  onApplyFilters,
  unitSystem,
  onOpenSettings,
  desktopDetail,
  selectedTripId,
  selectedTripIds,
  newTripIds,
  onToggleTripSelection,
}: {
  session: AuthSession
  trips: AccountTrip[]
  loading: boolean
  loadingEarlier: boolean
  hasEarlier: boolean
  error?: string
  earlierError?: string
  onRetry: () => void
  onLoadEarlier: () => void
  onOpenProfile: () => void
  onOpenTrip: (trip: AccountTrip) => void
  onShareTrip: (trip: AccountTrip) => void
  onEditTrip: (trip: AccountTrip) => void
  filters: AccountTripFilters
  onApplyFilters: (filters: AccountTripFilters) => void
  unitSystem: UnitSystem
  onOpenSettings: () => void
  desktopDetail?: ReactNode
  selectedTripId?: string | null
  selectedTripIds?: string[]
  newTripIds?: string[]
  onToggleTripSelection?: (trip: AccountTrip) => void
}) {
  const [layout, setLayout] = useState<TripListLayout>(readTripListLayout)
  const [desktopListWidth, setDesktopListWidth] = useState(readDesktopTripListWidth)
  const resizeGestureRef = useRef<{ pointerId: number; startX: number; startWidth: number } | null>(null)
  const changeLayout = (next: TripListLayout) => {
    setLayout(next)
    storeTripListLayout(next)
  }

  useEffect(() => {
    if (!desktopDetail) return
    const clampWidth = () => {
      const { minimum, maximum } = desktopTripListWidthBounds(window.innerWidth)
      setDesktopListWidth((width) => Math.min(maximum, Math.max(minimum, width)))
    }
    window.addEventListener('resize', clampWidth)
    return () => window.removeEventListener('resize', clampWidth)
  }, [desktopDetail])

  const resizeDesktopPanels = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = resizeGestureRef.current
    if (!gesture || gesture.pointerId !== event.pointerId) return
    const { minimum, maximum } = desktopTripListWidthBounds(window.innerWidth)
    setDesktopListWidth(Math.min(maximum, Math.max(minimum, gesture.startWidth + event.clientX - gesture.startX)))
  }

  const finishDesktopPanelResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (resizeGestureRef.current?.pointerId !== event.pointerId) return
    resizeGestureRef.current = null
    event.currentTarget.releasePointerCapture(event.pointerId)
    storeDesktopTripListWidth(desktopListWidth)
  }

  const resizeDesktopPanelsWithKeyboard = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home'].includes(event.key)) return
    event.preventDefault()
    const { minimum, maximum } = desktopTripListWidthBounds(window.innerWidth)
    const nextWidth = event.key === 'Home'
      ? defaultDesktopTripListWidth(window.innerWidth)
      : Math.min(maximum, Math.max(minimum, desktopListWidth + (event.key === 'ArrowRight' ? 16 : -16)))
    setDesktopListWidth(nextWidth)
    storeDesktopTripListWidth(nextWidth)
  }

  return (
    <main
      className={`account-shell account-trips${desktopDetail ? ' account-trips--desktop' : ''}`}
      style={desktopDetail ? { '--account-trip-list-width': `${desktopListWidth}px` } as CSSProperties : undefined}
    >
      <header className="account-header">
        <div className="account-header__inner">
          <div className="account-header__side"><BrandMark iconOnly href={import.meta.env.BASE_URL} /></div>
          <DesktopHeaderBrand />
          <div className="account-header__user">
            <button className="account-header__profile" type="button" aria-label="Open profile settings" title="Open profile settings" onClick={onOpenProfile}>
              <UserAvatar username={session.username} className="account-avatar" />
              <span><strong>{session.username}</strong><small>Signed in</small></span>
            </button>
            <HeaderActionsMenu actions={[
              { label: 'Settings', icon: <SettingsGearIcon />, onSelect: onOpenSettings },
              ...publicInformationMenuLinks(),
            ]} />
          </div>
        </div>
      </header>

      <section className={`account-trips__content${layout === 'grid' ? ' account-trips__content--grid' : ''}`} aria-labelledby="trips-title">
        <div className="account-trips__heading">
          <div>
            <p className="account-eyebrow">Your Mapapal network</p>
            <h1 id="trips-title">Trips</h1>
          </div>
          <div className="account-trips__heading-actions">
            <div className="account-layout-toggle" role="group" aria-label="Trip layout">
              <button type="button" aria-label="List view" title="Show trips as a list" aria-pressed={layout === 'list'} onClick={() => changeLayout('list')}><span aria-hidden="true">☰</span></button>
              <button type="button" aria-label="Grid view" title="Show trips in a grid" aria-pressed={layout === 'grid'} onClick={() => changeLayout('grid')}><span aria-hidden="true">▦</span></button>
            </div>
          </div>
        </div>

        <TripListFilters filters={filters} loading={loading} tripCount={!loading && !error ? trips.length : undefined} onApply={onApplyFilters} />

        {loading ? (
          <div className="account-trip-loading" aria-busy="true" aria-label="Loading your trips">
            <div className="account-trip-loading__brand" aria-hidden="true">
              <LoadingBrandPulse className="account-trip-loading__brand-icon" />
              <span>Loading..</span>
            </div>
          </div>
        ) : error ? (
          <div className="account-list-message" role="alert">
            <h2>Trips could not be loaded</h2>
            <p>{error}</p>
            <button className="account-primary-button" type="button" onClick={onRetry}>Try again</button>
          </div>
        ) : trips.length === 0 ? (
          <div className="account-list-message">
            <h2>{filtersAreActive(filters) ? 'No matching trips' : 'No trips yet'}</h2>
            <p>{filtersAreActive(filters) ? 'Try a different search or trip source.' : 'Start a trip in the Mapapal app and it will appear here.'}</p>
            {filtersAreActive(filters) ? <button className="account-primary-button" type="button" onClick={() => onApplyFilters({ ...DEFAULT_ACCOUNT_TRIP_FILTERS })}>Clear filters</button> : null}
          </div>
        ) : (
          <>
            <div className={`account-trip-list${layout === 'grid' ? ' account-trip-list--grid' : ''}`}>
              {trips.map((trip) => {
                const started = formatTripStarted(trip.startedAt)
                const ownTrip = trip.ownerName === session.username
                const selectedForMap = selectedTripIds?.includes(trip.id) ?? false
                const newTripIndex = newTripIds?.indexOf(trip.id) ?? -1
                const sharedTripUrl = shareUrlForTrip(trip, new URL(homeHref(), window.location.origin).href)
                return (
              <article
                className={`account-trip${ownTrip ? ' account-trip--own' : ''}${selectedForMap || selectedTripId === trip.id ? ' account-trip--selected' : ''}${newTripIndex >= 0 ? ' account-trip--new' : ''}`}
                key={trip.id}
                style={newTripIndex >= 0 ? { '--trip-entry-order': newTripIndex } as CSSProperties : undefined}
              >
                <button
                  className="account-trip__open"
                  type="button"
                  onClick={() => onOpenTrip(trip)}
                  aria-label={`Open ${trip.description || 'untitled trip'} by ${trip.ownerName}`}
                  title={`Open ${trip.description || 'untitled trip'} by ${trip.ownerName}`}
                  aria-current={selectedTripId === trip.id ? 'true' : undefined}
                />
                <div className="account-trip__topline">
                  <div className="account-trip__identity">
                    <UserAvatar username={trip.ownerName} className="account-trip__avatar" />
                    <div>
                      <p className="account-trip__owner">
                        {ownTrip ? <>{session.username} <span className="account-trip__owner-you">(You)</span></> : trip.ownerName}
                      </p>
                      <TripTitle title={trip.description || 'Untitled trip'} untitled={!trip.description} />
                    </div>
                  </div>
                  <div className="account-trip__topline-controls">
                    {ownTrip ? <TripActionsMenu label={trip.description || 'untitled trip'} onShare={() => onShareTrip(trip)} onEdit={() => onEditTrip(trip)} /> : null}
                    <div className="account-trip__topline-side">
                      <TripStatus status={trip.status} />
                      <span>{visibilityLabel(trip.visibility)}</span>
                    </div>
                    {onToggleTripSelection ? (
                      <div className="account-trip__desktop-actions">
                        <label
                          className="account-trip__compare"
                          title={selectedForMap ? 'Remove trip from comparison map' : 'Add trip to comparison map'}
                        >
                          <input
                            type="checkbox"
                            checked={selectedForMap}
                            aria-label={`${selectedForMap ? 'Remove' : 'Add'} ${trip.description || 'untitled trip'} ${selectedForMap ? 'from' : 'to'} comparison map`}
                            onChange={() => onToggleTripSelection(trip)}
                          />
                          <span aria-hidden="true" />
                        </label>
                        {sharedTripUrl ? (
                          <a
                            className="account-trip__open-shared"
                            href={sharedTripUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Open ${trip.description || 'untitled trip'} as a shared trip in a new tab`}
                            title="Open as shared trip in a new tab"
                          >
                            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 5h5v5M19 5l-8 8M17 13v5H6V7h5" /></svg>
                          </a>
                        ) : (
                          <span
                            className="account-trip__open-shared account-trip__open-shared--disabled"
                            role="img"
                            aria-label={`A shared trip link is unavailable for ${trip.description || 'untitled trip'}`}
                            title="Shared trip link unavailable"
                          >
                            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 5h5v5M19 5l-8 8M17 13v5H6V7h5" /></svg>
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="account-trip__main">
                  <div className="account-trip__metrics">
                    <span>
                      <small>Started</small>
                      <time dateTime={trip.startedAt} title={started.title}>
                        <strong>{started.label.replace(/^Started\s*/i, '')}</strong>
                      </time>
                    </span>
                    <span className="account-trip__metric--duration"><small>Duration</small><strong>{formatElapsed(trip.durationSeconds)}</strong></span>
                    <span><small>Distance</small><strong>{formatDistance(trip.distanceKilometers, unitSystem)}</strong></span>
                    <span><small>Average</small><strong>{formatSpeed(trip.averageSpeedKilometersPerHour, unitSystem)}</strong></span>
                  </div>
                </div>
              </article>
                )
              })}
            </div>
            {hasEarlier || earlierError ? (
              <div className="account-trip-list__more">
                {earlierError ? <p role="alert">{earlierError}</p> : null}
                <button type="button" onClick={onLoadEarlier} disabled={loadingEarlier}>
                  {loadingEarlier ? 'Loading earlier trips…' : earlierError ? 'Try loading earlier trips again' : 'Show earlier'}
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>
      {desktopDetail ? (
        <div
          className="account-trips__divider"
          role="separator"
          aria-label="Resize trip list and map"
          aria-orientation="vertical"
          aria-valuemin={desktopTripListWidthBounds(window.innerWidth).minimum}
          aria-valuemax={Math.round(desktopTripListWidthBounds(window.innerWidth).maximum)}
          aria-valuenow={Math.round(desktopListWidth)}
          tabIndex={0}
          title="Drag to resize trip list and map"
          onPointerDown={(event) => {
            resizeGestureRef.current = { pointerId: event.pointerId, startX: event.clientX, startWidth: desktopListWidth }
            event.currentTarget.setPointerCapture(event.pointerId)
          }}
          onPointerMove={resizeDesktopPanels}
          onPointerUp={finishDesktopPanelResize}
          onPointerCancel={finishDesktopPanelResize}
          onKeyDown={resizeDesktopPanelsWithKeyboard}
        ><span aria-hidden="true" /></div>
      ) : null}
      {desktopDetail ? <aside className="account-desktop-detail" aria-label="Selected trip map">{desktopDetail}</aside> : null}
    </main>
  )
}
